// src/services/patientService.js

import axios from "axios";
import moment from "moment";

const BACKEND_URL = "/api/paciente";

/**
 * Función para crear un nuevo paciente en el backend
 * @param {Object} datosPaciente - Datos recopilados del formulario
 * @param {Array} patologiasList - Lista completa de patologías para obtener nombres
 * @returns {Promise<Object>} - Respuesta del backend
 */
export const crearPaciente = async (datosPaciente, patologiasList) => {
  try {
    const {
      dataNutriologoSubdominio,
      formValuesBienvenida,
      formValuesBasic,
      formValuesSalud,
      formValuesComposicion,
      formValuesGlucosa,
      formValuesHablameDeTi,
    } = datosPaciente;

    // 1. Cálculo del IMC
    const estaturaMetros = formValuesBasic.estatura / 100; // Convertir cm a metros
    const imc = parseFloat(
      (
        parseFloat(formValuesBasic.pesoPaciente) /
        (estaturaMetros * estaturaMetros)
      ).toFixed(2)
    );

    let pesoIdeal;

    if (formValuesBasic.sexo.toLowerCase() === "m") {
      pesoIdeal =
        (formValuesBasic.estatura / 100) *
        (formValuesBasic.estatura / 100) *
        23;
    } else if (formValuesBasic.sexo.toLowerCase() === "f") {
      pesoIdeal =
        (formValuesBasic.estatura / 100) *
        (formValuesBasic.estatura / 100) *
        21.5;
    }

    pesoIdeal = parseFloat(pesoIdeal.toFixed(2));

    // 3. Fecha de registro en formato ISO
    const isoFechaRegistro = moment().toISOString();

    // 4. Fecha actual para la fechaUltimaCita en formato ISO
    const isoFechaUltimaCita = moment().toISOString();

    // 5. Preparar las patologías seleccionadas
    const patologiasPadecidas = Object.keys(formValuesSalud.patologias)
      .filter((clave) => formValuesSalud.patologias[clave])
      .map((clave) => {
        const patologia = patologiasList.find((p) => p.clave === clave);
        return patologia
          ? { nombre: patologia.nombre, clave: patologia.clave }
          : { nombre: clave, clave };
      });

    // 6. Preparar los grupos de alergias seleccionados
    const gruposAlergias = Object.keys(formValuesSalud.gruposAlergias).filter(
      (clave) => formValuesSalud.gruposAlergias[clave]
    );

    // 7. Preparar los contenidos de alergias seleccionados
    const contenidoAlergia = Object.keys(formValuesSalud.contenidos).filter(
      (clave) => formValuesSalud.contenidos[clave]
    );

    // 8. Estructurar la composición corporal
    //    En todos los campos de fecha que dependían de fechaRegistro.toISOString(),
    //    usaremos isoFechaRegistro directamente.
    const composicionCorporal = {
      biometriaHematica: {
        fecha: formValuesComposicion.biometriaHematicaFecha || isoFechaRegistro,
        hemoglobina: {
          cantidad: formValuesComposicion.hemoglobina || "",
          medida: formValuesComposicion.hemoglobinaUnidad || "",
        },
      },
      perfilLipidico: {
        fecha: formValuesComposicion.perfilLipidicoFecha || isoFechaRegistro,
        trigliceridos: {
          cantidad: formValuesComposicion.trigliceridos || "",
          medida: formValuesComposicion.trigliceridosMedida || "",
        },
        colesterolTotal: {
          cantidad: formValuesComposicion.colesterolTotal || "",
          medida: formValuesComposicion.colesterolTotalMedida || "",
        },
        colesterolLDL: {
          cantidad: formValuesComposicion.colesterolLDL || "",
          medida: formValuesComposicion.colesterolLDLMedida || "",
        },
        colesterolHDL: {
          cantidad: formValuesComposicion.colesterolHDL || "",
          medida: formValuesComposicion.colesterolHDLMedida || "",
        },
        colesterolVLDL: {
          cantidad: formValuesComposicion.colesterolVLDL || "",
          medida: formValuesComposicion.colesterolVLDLMedida || "",
        },
      },
      quimicaSanguinea: {
        fecha: formValuesComposicion.quimicaSanguineaFecha || isoFechaRegistro,
        glucosa: {
          cantidad: formValuesComposicion.glucosa || "",
          medida: formValuesComposicion.glucosaMedida || "",
        },
        creatinina: {
          cantidad: formValuesComposicion.creatinina || "",
          medida: formValuesComposicion.creatininaMedida || "",
        },
        albumina: {
          cantidad: formValuesComposicion.albumina || "",
          medida: formValuesComposicion.albuminaMedida || "",
        },
        urea: {
          cantidad: formValuesComposicion.urea || "",
          medida: formValuesComposicion.ureaMedida || "",
        },
        acidoUrico: {
          cantidad: formValuesComposicion.acidoUrico || "",
          medida: formValuesComposicion.acidoUricoMedida || "",
        },
        hierro: {
          cantidad: formValuesComposicion.hierro || "",
          medida: formValuesComposicion.hierroMedida || "",
        },
        glucosaPlasma: {
          cantidad: formValuesComposicion.glucosaPlasma || "",
          medida: formValuesComposicion.glucosaPlasmaMedida || "",
        },
      },
      pruebasDeGlucosa: {
        glucosaCualquierHora: {
          fecha:
            formValuesGlucosa.glucosaCualquierHoraFecha || isoFechaRegistro,
          cantidad: formValuesGlucosa.glucosaCualquierHora || "",
          medida: formValuesGlucosa.glucosaCualquierHoraMedida || "",
        },
        glucosaAyuno: {
          fecha: formValuesGlucosa.glucosaAyunoFecha || isoFechaRegistro,
          cantidad: formValuesGlucosa.glucosaAyuno || "",
          medida: formValuesGlucosa.glucosaAyunoMedida || "",
        },
        hemoglobinaGlucosilada: {
          fecha:
            formValuesGlucosa.hemoglobinaGlucosiladaFecha || isoFechaRegistro,
          cantidad: formValuesGlucosa.hemoglobinaGlucosilada || "",
          medida: formValuesGlucosa.hemoglobinaGlucosiladaMedida || "",
        },
        toleranciaOralGlucosa: {
          fecha: formValuesGlucosa.toleranciaGlucosaFecha || isoFechaRegistro,
          cantidad: formValuesGlucosa.toleranciaGlucosa || "",
          medida: formValuesGlucosa.toleranciaGlucosaMedida || "",
        },
      },
      examenGeneralOrina: {
        fecha:
          formValuesComposicion.examenGeneralOrinaFecha || isoFechaRegistro,
        glucosaOrina: {
          cantidad: formValuesComposicion.glucosaOrina || "",
          medida: formValuesComposicion.glucosaOrinaMedida || "",
        },
        proteinasOrina: {
          cantidad: formValuesComposicion.proteinasOrina || "",
          medida: formValuesComposicion.proteinasOrinaMedida || "",
        },
        hemoglobinaOrina: {
          cantidad: formValuesComposicion.hemoglobinaOrina || "",
          medida: formValuesComposicion.hemoglobinaOrinaMedida || "",
        },
        urobilinogeno: {
          cantidad: formValuesComposicion.urobilinogeno || "",
          medida: formValuesComposicion.urobilinogenoMedida || "",
        },
        cuerposCetonicos: {
          // cantidad: formValuesComposicion.cuerposCetonicos || "",
          cantidad: "",
          medida: formValuesComposicion.cuerposCetonicosMedida || "",
        },
        pigmentosBiliares: {
          // cantidad: formValuesComposicion.pigmentosBiliares || "",
          cantidad: "",
          medida: formValuesComposicion.pigmentosBiliaresMedida || "",
        },
        nitritos: {
          // cantidad: formValuesComposicion.nitritos || "",
          cantidad: "",
          medida: formValuesComposicion.nitritosMedida || "",
        },
      },
      indicesCorporales: {
        fecha: formValuesComposicion.indicesCorporalesFecha || isoFechaRegistro,
        circunferenciaCintura: {
          cantidad: formValuesComposicion.circunferenciaCintura || "",
          medida: formValuesComposicion.circunferenciaCinturaMedida || "",
        },
        porcentajeGrasa: {
          cantidad: formValuesComposicion.porcentajeGrasa || "",
          medida: formValuesComposicion.porcentajeGrasaMedida || "",
        },
        porcentajeAgua: {
          cantidad: formValuesComposicion.porcentajeAgua || "",
          medida: formValuesComposicion.porcentajeAguaMedida || "",
        },
        porcentajeMusculo: {
          cantidad: formValuesComposicion.porcentajeMusculo || "",
          medida: formValuesComposicion.porcentajeMusculoMedida || "",
        },
        edadDigestion: {
          cantidad: formValuesComposicion.edadDigestion || "",
          medida: formValuesComposicion.edadDigestionMedida || "",
        },
        grasaVisceral: {
          cantidad: formValuesComposicion.grasaVisceral || "",
          medida: formValuesComposicion.grasaVisceralMedida || "",
        },
      },
      antropometria: {
        fecha: formValuesComposicion.antropometriaFecha || isoFechaRegistro,
        brazo: {
          cantidad: formValuesComposicion.brazo || "",
          medida: formValuesComposicion.brazoMedida || "",
        },
        hombros: {
          cantidad: formValuesComposicion.hombros || "",
          medida: formValuesComposicion.hombrosMedida || "",
        },
        pectoralBusto: {
          cantidad: formValuesComposicion.pectoralBusto || "",
          medida: formValuesComposicion.pectoralBustoMedida || "",
        },
        abdomenCintura: {
          cantidad: formValuesComposicion.abdomenCintura || "",
          medida: formValuesComposicion.abdomenCinturaMedida || "",
        },
        caderaGluteos: {
          cantidad: formValuesComposicion.caderaGluteos || "",
          medida: formValuesComposicion.caderaGluteosMedida || "",
        },
        muslo: {
          cantidad: formValuesComposicion.muslo || "",
          medida: formValuesComposicion.musloMedida || "",
        },
        pantorrilla: {
          cantidad: formValuesComposicion.pantorrilla || "",
          medida: formValuesComposicion.pantorrillaMedida || "",
        },
      },
      plieguesCutaneos: {
        fecha: formValuesComposicion.plieguesCutaneosFecha || isoFechaRegistro,
        pectoral: {
          cantidad: formValuesComposicion.pectoral || "",
          medida: formValuesComposicion.pectoralMedida || "",
        },
        tricipital: {
          cantidad: formValuesComposicion.tricipital || "",
          medida: formValuesComposicion.tricipitalMedida || "",
        },
        bicipital: {
          cantidad: formValuesComposicion.bicipital || "",
          medida: formValuesComposicion.bicipitalMedida || "",
        },
        subescapular: {
          cantidad: formValuesComposicion.subescapular || "",
          medida: formValuesComposicion.subescapularMedida || "",
        },
        midaxilar: {
          cantidad: formValuesComposicion.midaxilar || "",
          medida: formValuesComposicion.midaxilarMedida || "",
        },
        suprailiaco: {
          cantidad: formValuesComposicion.suprailiaco || "",
          medida: formValuesComposicion.suprailiacoMedida || "",
        },
        abdominal: {
          cantidad: formValuesComposicion.abdominal || "",
          medida: formValuesComposicion.abdominalMedida || "",
        },
        musloMedio: {
          cantidad: formValuesComposicion.musloMedio || "",
          medida: formValuesComposicion.musloMedioMedida || "",
        },
        pantorrillaMedia: {
          cantidad: formValuesComposicion.pantorrillaMedia || "",
          medida: formValuesComposicion.pantorrillaMediaMedida || "",
        },
      },
    };
    // 9. Construir el JSON completo
    const pacienteData = {
      pacienteOnline: true,
      primeraConsulta: false,
      nutriologoId: dataNutriologoSubdominio.nutriologoInfo._id,
      correo: formValuesBasic.email,
      password: formValuesBienvenida.password,
      nombrePaciente: formValuesBasic.nombrePaciente,
      apellidoPaciente: formValuesBasic.apellidoPaciente,
      imc: imc,
      edad: parseInt(formValuesBasic.edad, 10),
      telefono: formValuesBienvenida.telefono,
      pesoPaciente: formValuesBasic.pesoPaciente,
      fechaNacimiento: formValuesBasic.fechaNacimiento,
      sexo: formValuesBasic.sexo,
      embarazoActivo: formValuesBasic.embarazoActivo,
      tiempoEmbarazo: formValuesBasic.tiempoEmbarazo,
      estatura: formValuesBasic.estatura,
      pesoIdeal: pesoIdeal,
      fechaRegistro: isoFechaRegistro,
      historiaClinica: {
        preguntas: Object.keys(formValuesHablameDeTi).map((nombre) => ({
          nombre,
          respuesta: formValuesHablameDeTi[nombre],
        })),
      },
      patologiasPadecidas: patologiasPadecidas,
      gruposAlergias: gruposAlergias,
      contenidoAlergia: contenidoAlergia.map((nombre) => nombre),
      composicionCorporal: composicionCorporal,
      actividadDiaria: "",
      anotacionesDelPaciente: ["0"],
      cantidadEjercicio: "",
      fechaProximaCita: "",
      fechaUltimaCita: isoFechaUltimaCita,
      preguntaEjercicio: "no",
    };

    // 10. Realizar la solicitud POST al backend
    const response = await axios.post(BACKEND_URL, pacienteData);

    return response.data;
  } catch (error) {
    console.error("Error al crear el paciente:", error);
    throw error;
  }
};
